import { Label, Textarea, Button } from 'flowbite-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { createNoticeBanner, getNoticeBannersByFilter, updateNoticeBannerById, updateStatusNoticeBannerById } from 'api/noticeBannerApi';
import { pageType } from 'utils/constants';
import { useSelector } from 'react-redux';
import { orgSelector } from 'redux/selectors';
import { messageErrors } from 'utils/utils';
import GroupButton from 'components/button/groupButton';

const NoticeBannerPage = (props: any) => {
  const { type } = props;
  const [t] = useTranslation();
  const queryClient = useQueryClient()
  const {organizationId} = useSelector(orgSelector);
  const [noticeBanner, setNoticeBanner] = useState<any>(null);

  const ValidateSchema = Yup.object().shape({
    message: Yup.string().required(t('userManagementPage.requiredField')).max(5000, t('noticeBannerPage.maxLength5000')),
  });

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
    setValue
  }: any = useForm({
    mode: 'onChange',
    resolver: yupResolver(ValidateSchema),
  });

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ['get-notice-banners'],
    queryFn: () => getNoticeBannersByFilter({
      page: 0,
      limit: 1,
      organizationId: type === pageType.SITE ? '' : organizationId,
      type: (type as string).toUpperCase()
    }),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (data) {
      setNoticeBanner(data.data?.entities[0]);
      reset(data.data?.entities[0]);
    } else {
      reset();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!noticeBanner) {
      setValue('message', '');
    } else {
      reset();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeBanner])

  useEffect(() => {
    refetch();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);
 

  const handleAddAndUpdate = async (payload: any) => {
    if (noticeBanner?.id) {
      const request: any = {
        ...payload,
        id: noticeBanner?.id
      };

      await updateNoticeBannerById(noticeBanner?.id, request);
    }
    else {
      payload.organizationId = type === pageType.SITE ? null : organizationId;
      payload.organization = type === pageType.SITE ? null : { id: organizationId };
      await createNoticeBanner(payload);
    }
  }

  const mutation = useMutation(['create-update-reason'], {mutationFn: handleAddAndUpdate});

  const onSubmit = (value: any) => {
    const payload = {
      ...value,
      type: (type as string).toUpperCase(),
      bannerType: "Warning"
    }
    mutation.mutate(payload,
       {
        onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['get-notice-banners'] });
        toast.success(`${t('noticeBannerPage.editSuccess')}`);
        }})
  }

  const handleSuccess = (message: string) => {
    toast.success(message);
    refetch();
    queryClient.invalidateQueries(['get-notice-banners']);
  };

  const handleonError = (error: string) => {
    const message: string = messageErrors(error, t);
    toast.error(message);
  };

  const updateStatus = useMutation(['updateStatusNoticeBanner'], {
    mutationFn: updateStatusNoticeBannerById,
    onSuccess: () => handleSuccess(t('noticeBannerPage.changeStatusSuccess')),
    onError: handleonError,
  });

  const textStatusLabel = noticeBanner?.isActive ? 'text-red-900' : 'text-green-500';
  const bgStatusLabel = noticeBanner?.isActive ? 'bg-red-50' : 'bg-green-50';
  const bgStatusButton = noticeBanner?.isActive ? 'bg-red-700 enabled:hover:bg-red-600' : 'bg-green-500 enabled:hover:bg-green-400';

  return (
    <div className="w-full px-8">
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex-col">
        <div className="mb-4 mt-4">
        <div className={`flex-1 input-text relative`}>
          <span className={`holder ${noticeBanner?.message !== "" ? 'label' : ''}`}>{`${t('noticeBannerPage.message')} *`}</span>
          <Textarea
              color={errors?.message ? 'failure' : 'gray'}
              id="message"
              name="message"
              rows={6}
              //placeholder={`${t('noticeBannerPage.message')} *`}
              // eslint-disable-next-line
              {...register('message')}
              className="text-sm rounded-[2px]"
              helperText={<span className="text-xs">{errors?.description?.message}</span>}
          />
        </div>
        {errors?.message && <div className={`veri-modal height-16 mt-1 text-xs font-normal text-red-600`}>{errors?.message?.message}</div>}
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <GroupButton
                className="pt-1 pb-2"
                buttons={[
                  {
                    type: 'submit',
                    text: t('noticeBannerPage.sendMessage'),
                    classType: 'blue',
                    isLoading: false,
                  },
                ]}
          />
      </div>
    </form>
    {noticeBanner?.message && (
      <div className={`${bgStatusLabel} w-full mt-8 p-8 rounded-lg flex justify-between action`}>
        <div>
          <div className={`text-xl font-semibold ${textStatusLabel} title pb-3`}>
            {noticeBanner?.isActive ? t('noticeBannerPage.deactivateTitle') : t('noticeBannerPage.activateTitle')}
          </div>
          <div className={`${textStatusLabel} content`}>
            {noticeBanner?.isActive ? t('noticeBannerPage.deactivateDescrition') : t('noticeBannerPage.activateDescrition')}
          </div>
        </div>
        <div className="flex items-center justify-end ml-2">
          <Button
            type="button"
            size="sm"
            className={`${bgStatusButton} text-sm`}
            onClick={() => updateStatus.mutate({ id: noticeBanner?.id, isActive: !noticeBanner?.isActive })}
          >
            {noticeBanner?.isActive ? t('deactivate') : t('activate')}
          </Button>
        </div>
  </div>
    )}
    </div>
              

  );
};
export default NoticeBannerPage;